import axios from 'axios';
import { getApiUrl } from '../../../config/api';

const LIVE_MEET_ACTOR_EMAIL_KEYS = [
  'admin_email',
  'employer_email',
  'recruiter_email',
  'company_email',
  'organizer_email',
];

const getStoredActorEmail = (key) => {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem(key) || window.localStorage.getItem(key) || '';
};

const getActorEmail = () => {
  if (typeof window === 'undefined') return '';
  for (const key of LIVE_MEET_ACTOR_EMAIL_KEYS) {
    const value = getStoredActorEmail(key);
    if (value) {
      return value.trim().toLowerCase();
    }
  }
  return '';
};

export { getActorEmail };

const liveMeetBookingInflight = new Map();

export async function fetchLiveMeetBooking({
  candidateEmail,
  jobId,
  includeTimeline = false,
} = {}) {
  const email = String(candidateEmail || '').trim().toLowerCase();
  const job = String(jobId || '').trim();
  const cacheKey = `${job}::${email}::${includeTimeline ? 'timeline' : 'lite'}`;

  if (liveMeetBookingInflight.has(cacheKey)) {
    return liveMeetBookingInflight.get(cacheKey);
  }

  const request = axios
    .get(getApiUrl('/api/meeting/live-meet/booking'), {
      params: {
        candidate_email: email,
        job_id: job,
        include_timeline: includeTimeline ? '1' : '0',
      },
      withCredentials: true,
    })
    .then((response) => response.data)
    .finally(() => {
      liveMeetBookingInflight.delete(cacheKey);
    });

  liveMeetBookingInflight.set(cacheKey, request);
  return request;
}

export async function fetchLiveMeetInterviewRecording({ candidateEmail, jobId }) {
  const response = await axios.get(getApiUrl('/api/meeting/live-meet/interview-recording'), {
    params: {
      candidate_email: candidateEmail,
      job_id: String(jobId || ''),
    },
    withCredentials: true,
  });
  return response.data;
}

function formatManualScheduleError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 405) {
    return (
      'Manual scheduling API is not available on the backend (HTTP 405). ' +
      'Restart the Web-Interviewer Flask server on port 5000, then try again.'
    );
  }
  if (typeof data?.error === 'string' && data.error) {
    return data.error;
  }
  if (status && err?.message) {
    return err.message;
  }
  return err?.message || 'Failed to schedule meeting';
}

export async function createManualSchedule(payload) {
  try {
    const response = await axios.post(
      getApiUrl('/api/meeting/manual-schedule'),
      {
        ...payload,
        scheduling_mode: payload.scheduling_mode || 'manual',
        meeting_provider: payload.meeting_provider || 'live_meet',
        manually_created_by: payload.manually_created_by || getActorEmail(),
      },
      { withCredentials: true, timeout: 120000 }
    );
    return response.data;
  } catch (err) {
    const message = formatManualScheduleError(err);
    const wrapped = new Error(message);
    wrapped.response = err?.response;
    throw wrapped;
  }
}

export async function createManualLiveMeet(payload) {
  return createManualSchedule({
    ...payload,
    meeting_provider: payload.meeting_provider || 'live_meet',
    scheduling_mode: 'manual',
  });
}

/**
 * Open the Live Interview room on the SearchJobAI SPA (port 3000), not the Flask API (5000).
 */
export function openLiveMeetRoomFromBooking(booking) {
  if (typeof window === 'undefined' || !booking) return false;

  const roomId = booking.live_room_id || booking.live_room?.room_id;
  if (roomId) {
    const params = new URLSearchParams();
    if (booking.booking_id) {
      params.set('booking_id', String(booking.booking_id));
    }
    const query = params.toString();
    window.open(
      `${window.location.origin}/interview-room/${encodeURIComponent(roomId)}${query ? `?${query}` : ''}`,
      '_blank',
      'noopener,noreferrer'
    );
    return true;
  }

  const candidates = [
    booking.interviewer_url,
    booking.join_url,
    booking.interviewer_join_url,
  ].filter(Boolean);

  for (const raw of candidates) {
    try {
      const url = new URL(String(raw), window.location.origin);
      if (!url.pathname.includes('/interview-room/')) continue;
      if (url.pathname.includes('/api/meeting/')) continue;
      const target =
        url.hostname === window.location.hostname && url.port === window.location.port
          ? url.toString()
          : `${window.location.origin}${url.pathname}${url.search}${url.hash}`;
      window.open(target, '_blank', 'noopener,noreferrer');
      return true;
    } catch {
      // try next candidate
    }
  }
  return false;
}

export async function fetchLiveMeetTranscript({ liveRoomId, bookingId } = {}) {
  const response = await axios.get(getApiUrl('/api/meeting/live-meet/transcript'), {
    params: {
      live_room_id: liveRoomId || undefined,
      booking_id: bookingId || undefined,
      actor_email: getActorEmail() || undefined,
      role: 'interviewer',
    },
    withCredentials: true,
  });
  return response.data;
}

function normalizeTranscriptPdfPayload(payload) {
  const pdfUrl = String(payload?.pdf_url || payload?.transcript_pdf?.url || '').trim();
  return {
    ...payload,
    pdf_url: pdfUrl,
    transcript_pdf: {
      ...(payload?.transcript_pdf || {}),
      url: pdfUrl,
      expires_in_seconds:
        payload?.expires_in ?? payload?.transcript_pdf?.expires_in_seconds ?? null,
    },
  };
}

export async function fetchLiveMeetTranscriptPdfUrl({ liveRoomId } = {}) {
  if (!liveRoomId) {
    throw new Error('Search Job Meet room id is required before opening the transcript PDF.');
  }
  const params = {
    actor_email: getActorEmail() || undefined,
    role: 'interviewer',
  };
  const requestConfig = {
    params,
    withCredentials: true,
    validateStatus: () => true,
  };
  const roomPath = `/api/live-interview/rooms/${encodeURIComponent(liveRoomId)}`;
  let response = await axios.get(getApiUrl(`${roomPath}/transcript/pdf-url`), requestConfig);
  if (response.status === 404 || response.status === 405) {
    response = await axios.get(getApiUrl(`${roomPath}/transcript-pdf-url`), requestConfig);
  }
  const payload = response.data || {};
  if (response.status >= 400 || payload?.success === false) {
    const error = new Error(
      payload?.message || payload?.error || `Transcript PDF request failed (${response.status})`
    );
    error.response = response;
    throw error;
  }
  return normalizeTranscriptPdfPayload(payload);
}

export async function updateLiveMeetBookingAction({ bookingId, action }) {
  try {
    const response = await axios.post(
      getApiUrl('/api/meeting/booking/action'),
      {
        booking_id: String(bookingId || '').trim(),
        action: String(action || '').trim().toLowerCase(),
        actor_email: getActorEmail() || undefined,
      },
      { withCredentials: true, timeout: 120000 }
    );
    return response.data;
  } catch (err) {
    const message =
      err?.response?.data?.error ||
      err?.message ||
      'Failed to update the interview booking';
    const wrapped = new Error(message);
    wrapped.response = err?.response;
    throw wrapped;
  }
}

export async function rescheduleLiveMeetBooking(payload) {
  try {
    const response = await axios.post(
      getApiUrl('/api/meeting/live-meet/reschedule'),
      {
        ...payload,
        booking_id: String(payload?.booking_id || payload?.bookingId || '').trim(),
        actor_email: getActorEmail() || undefined,
      },
      { withCredentials: true, timeout: 120000 }
    );
    return response.data;
  } catch (err) {
    const message = formatManualScheduleError(err);
    const wrapped = new Error(message);
    wrapped.response = err?.response;
    throw wrapped;
  }
}

export async function cancelLiveMeetBooking({ bookingId, booking_id, reason } = {}) {
  try {
    const response = await axios.post(
      getApiUrl('/api/meeting/booking/cancel'),
      {
        booking_id: String(booking_id || bookingId || '').trim(),
        actor_email: getActorEmail() || undefined,
        reason: String(reason || '').trim(),
      },
      { withCredentials: true, timeout: 120000 }
    );
    return response.data;
  } catch (err) {
    const message = formatManualScheduleError(err);
    const wrapped = new Error(message);
    wrapped.response = err?.response;
    throw wrapped;
  }
}

export async function sendLiveMeetConfirmationReminder({ bookingId, participant } = {}) {
  try {
    const response = await axios.post(
      getApiUrl('/api/meeting/live-meet/confirmation-reminder'),
      {
        booking_id: String(bookingId || '').trim(),
        participant: String(participant || '').trim().toLowerCase() || undefined,
        actor_email: getActorEmail() || undefined,
      },
      { withCredentials: true, timeout: 120000 }
    );
    return response.data;
  } catch (err) {
    const message =
      err?.response?.data?.error ||
      err?.message ||
      'Failed to send the confirmation reminder';
    const wrapped = new Error(message);
    wrapped.response = err?.response;
    throw wrapped;
  }
}

export async function proceedLiveMeetWithoutConfirmations({ bookingId } = {}) {
  try {
    const response = await axios.post(
      getApiUrl('/api/meeting/live-meet/proceed-without-confirmations'),
      {
        booking_id: String(bookingId || '').trim(),
        actor_email: getActorEmail() || undefined,
      },
      { withCredentials: true, timeout: 120000 }
    );
    return response.data;
  } catch (err) {
    const message =
      err?.response?.data?.error ||
      err?.message ||
      'Failed to proceed without confirmations';
    const wrapped = new Error(message);
    wrapped.response = err?.response;
    throw wrapped;
  }
}

export async function createInstantLiveMeet(payload) {
  try {
    const response = await axios.post(
      getApiUrl('/api/meeting/live-meet/instant'),
      {
        ...payload,
        scheduling_mode: payload.scheduling_mode || 'instant',
        meeting_provider: payload.meeting_provider || 'live_meet',
        manually_created_by: payload.manually_created_by || getActorEmail(),
      },
      { withCredentials: true, timeout: 120000 }
    );
    return response.data;
  } catch (err) {
    const message = formatManualScheduleError(err);
    const wrapped = new Error(message);
    wrapped.response = err?.response;
    throw wrapped;
  }
}
